import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import IOSInstallPrompt from '../components/IOSInstallPrompt';

/*  tweddi — /join (code entry)
    Identity: Inclusive Sans, Ink #141313 / Cream #FFFDF8, apricot sparingly.
    Hero = the official tweddi logo animation (exact geometry): band 1 draws +
    spins in → band 2 rolls in → LOCK (weave + ripple + sparks + shake) →
    "tweddi" rises letter by letter → idle breathing. Real wiring preserved.  */

const API   = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INK   = '#141313';
const CREAM = '#FFFDF8';
const MUTE  = 'rgba(255,253,248,0.55)';
const APRICOT = '#F4A26B';

export default function LandingPage() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [eventCode, setEventCode] = useState('');
  const [isJoining, setIsJoining] = useState(false);
  const [foundEvent, setFoundEvent] = useState(null);   // validated wedding
  const stageRef = useRef(null);

  // If the guest arrived with a code (e.g. tweddi.com/?code=ABC123 -> RootGate
  // stashed it, or they were bounced here mid-flow), don't make them retype or
  // even tap — look the wedding up for them and go straight to confirmation.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let pending = null;
      try { pending = localStorage.getItem('pendingEventCode'); } catch {}
      if (!pending) return;
      const code = pending.toUpperCase();
      setEventCode(code);
      try {
        const res = await axios.get(`${API}/countdown/${code}`);
        if (!cancelled) setFoundEvent({ ...res.data, code });
      } catch {
        // stale/broken code — clear it and let them type a fresh one
        try { localStorage.removeItem('pendingEventCode'); } catch {}
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // play the logo sequence on mount, then settle into idle breathing
  useEffect(() => {
    const st = stageRef.current; if (!st) return;
    st.classList.remove('play', 'breathe');
    void st.offsetWidth;                       // force reflow so anim restarts
    st.classList.add('play');
    const b = setTimeout(() => st.classList.add('breathe'), 3200);
    return () => clearTimeout(b);
  }, []);

  // STEP 1 — check the code is real BEFORE we ask anyone to sign up.
  // /countdown/{code} is public (no auth), so a wrong code fails here in two
  // seconds instead of after an account + email verification.
  const handleJoinEvent = async () => {
    const code = eventCode.trim().toUpperCase();
    if (!code) { toast.error('Please enter your wedding code'); return; }
    setIsJoining(true);
    try {
      const res = await axios.get(`${API}/countdown/${code}`);
      setFoundEvent({ ...res.data, code });          // -> show the confirm card
    } catch (err) {
      toast.error(err.response?.status === 404
        ? "We couldn't find that wedding. Check the code and try again."
        : (err.response?.data?.detail || 'Invalid wedding code'));
    } finally { setIsJoining(false); }
  };

  // STEP 2 — they've seen the wedding and confirmed. Now sign in / sign up.
  const handleContinue = async () => {
    const code = foundEvent.code;
    if (!user) {
      localStorage.setItem('pendingEventCode', code);
      navigate('/auth');                              // Auth joins them after signup
      return;
    }
    setIsJoining(true);
    try {
      const res = await axios.post(`${API}/events/join`, { event_code: code });
      updateUser({ event_id: res.data.event.id });
      toast.success(`Joined ${res.data.event.bride_name} & ${res.data.event.groom_name}'s wedding!`);
      navigate(user.profile_complete ? '/discover' : '/profile-setup');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not join this wedding');
    } finally { setIsJoining(false); }
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter') handleJoinEvent(); };

  // "Get your code" — the HOST path: signup -> pay -> create -> code issued.
  const handleHostClick = async () => {
    if (user?.is_host) { navigate('/admin'); return; }
    if (user && !user.is_host) {
      try {
        await axios.post(`${API}/auth/upgrade-to-host`);
        updateUser({ is_host: true });
        navigate('/admin');
      } catch { toast.error('Failed to upgrade account'); }
      return;
    }
    localStorage.setItem('isHostSignup', 'true');
    navigate('/auth');
  };

  return (
    <div style={S.page} data-testid="landing-page">
      <style>{CSS}</style>
      <IOSInstallPrompt />

      {/* top bar */}
      <header style={S.nav} className="tw-chrome">
        <div style={S.brandSmall} onClick={() => navigate('/')} role="button">
          <svg viewBox="-60 -60 664 520" width="26" height={26*(520/664)} style={{ overflow: 'visible', display: 'block' }} aria-label="tweddi">
            <circle cx="200" cy="200" r="108" fill="none" stroke={CREAM} strokeWidth="66" />
            <circle cx="344" cy="200" r="108" fill="none" stroke={INK} strokeWidth="108" />
            <circle cx="344" cy="200" r="108" fill="none" stroke={CREAM} strokeWidth="66" />
            <path d="M 206.01 307.83 A 108 108 0 0 0 282.00 270.28" fill="none" stroke={INK} strokeWidth="108" />
            <path d="M 196.23 307.93 A 108 108 0 0 0 285.11 266.49" fill="none" stroke={CREAM} strokeWidth="66" />
          </svg>
          <span style={S.brandSmallWord}>tweddi</span>
        </div>
        {user ? (
          <span className="tw-nl" onClick={() => navigate(user.is_host ? '/admin' : '/discover')} data-testid="dashboard-btn">
            {user.is_host ? 'My event' : 'Discover'}
          </span>
        ) : (
          <span className="tw-nl" onClick={() => navigate('/discover-tweddi')} data-testid="discover-tweddi-btn">
            Discover tweddi
          </span>
        )}
      </header>

      {/* center stage */}
      <main style={S.main}>
        <div className="stage" ref={stageRef} style={S.stage}>
          <svg viewBox="-60 -60 664 520" shapeRendering="geometricPrecision" style={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible' }} aria-label="tweddi logo — two bands interlocking">
            <g id="mark">
              <circle id="ripple"  cx="272" cy="200" r="230" />
              <circle id="ripple2" cx="272" cy="200" r="290" />
              <line id="sparkA" className="spark" x1="268" y1="128" x2="276" y2="112" />
              <line id="sparkB" className="spark" x1="268" y1="276" x2="262" y2="292" />

              {/* ACT I — band 1 draws + clean copy */}
              <g id="band1"><circle className="ring" pathLength="100" cx="200" cy="200" r="108" /></g>
              <g id="band1clean"><circle className="ring" cx="200" cy="200" r="108" /></g>

              {/* ACT II — band 2 rolls in */}
              <g id="band2roll"><circle className="ring" cx="344" cy="200" r="108" /></g>

              {/* ACT III — the woven lock */}
              <g id="lockLayers">
                <circle className="halo" cx="344" cy="200" r="108" />
                <circle className="ring" cx="344" cy="200" r="108" />
                <path className="halo" d="M 206.01 307.83 A 108 108 0 0 0 282.00 270.28" fill="none" />
                <path className="ring" d="M 196.23 307.93 A 108 108 0 0 0 285.11 266.49" fill="none" />
              </g>
            </g>
          </svg>
        </div>

        {/* wordmark rises letter by letter */}
        <div className="word" aria-hidden="true">
          <span>t</span><span>w</span><span>e</span><span>d</span><span>d</span><span>i</span>
        </div>

        {!foundEvent ? (
          <>
            <p className="tw-late" style={S.sub}>
              The wedding introductions.<br />
              <span style={S.subDim}>Meet the other single guests — before, during, and for 30 days after the wedding.</span>
            </p>

            {/* code field */}
            <div className="tw-late2" style={S.join}>
              <input
                value={eventCode}
                onChange={(e) => setEventCode(e.target.value.toUpperCase())}
                onKeyDown={handleKeyDown}
                placeholder="ENTER CODE"
                maxLength={10}
                style={S.input}
                data-testid="event-code-input"
              />
              <button
                style={S.go}
                className="tw-go"
                onClick={handleJoinEvent}
                disabled={isJoining}
                data-testid="join-event-btn"
                aria-label="Find your wedding"
              >
                {isJoining ? (
                  <span className="spin" style={{ width: 16, height: 16, border: '1.5px solid rgba(20,19,19,0.4)', borderTopColor: INK, borderRadius: '50%', display: 'inline-block' }} />
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={INK} strokeWidth="2"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                )}
              </button>
            </div>

            <p className="tw-late2" style={S.helper}>A guest? The couple sent you a code.</p>

            {/* the other path — getting married */}
            <div className="tw-late3" style={S.divider}>
              <span style={S.dividerLine} />
              <span style={S.dividerWord}>or</span>
              <span style={S.dividerLine} />
            </div>

            <button className="tw-late3 tw-host" onClick={handleHostClick} style={S.hostBtn} data-testid="host-wedding-btn">
              <span style={S.hostLabel}>Getting married?</span>
              <span style={S.hostCta}>
                Get your wedding code
                <span className="tw-hostArrow" style={S.hostArrow}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={INK} strokeWidth="2.2"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                </span>
              </span>
            </button>

            <p className="tw-late3" style={S.priceNote}>
              <span style={S.priceDot} />€99 one-time payment
            </p>

            {/* quiet proof */}
            <div className="tw-late4" style={S.proof}>
              <span style={S.proofItem}><span style={S.dot} />Private to your wedding</span>
              <span style={S.proofItem}><span style={S.dot} />Free for guests</span>
              <span style={S.proofItem}><span style={S.dot} />No public profiles</span>
            </div>
          </>
        ) : (
          /* ── the code was real: show them the wedding before asking anything ── */
          <div className="tw-found" style={S.found}>
            <p style={S.foundEyebrow}><span style={S.priceDot} />Wedding found</p>
            <h1 style={S.foundName}>{foundEvent.event_name}</h1>

            <div style={S.foundMeta}>
              {foundEvent.wedding_date && (
                <span>{new Date(foundEvent.wedding_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
              )}
              {foundEvent.venue && <><span style={S.metaDot} /><span>{foundEvent.venue}</span></>}
            </div>

            <div style={S.foundStats}>
              <span><strong style={S.statNum}>{foundEvent.guests_joined ?? 0}</strong> already joined</span>
              <span style={S.metaDot} />
              <span><strong style={S.statNum}>{foundEvent.matches_made ?? 0}</strong> matches so far</span>
            </div>

            <button className="tw-go2" onClick={handleContinue} disabled={isJoining} style={S.foundCta} data-testid="confirm-join-btn">
              {isJoining ? 'Joining…' : (user ? 'Join this wedding' : 'Create your profile')}
              <span style={S.foundArrow}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={INK} strokeWidth="2.2"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
              </span>
            </button>

            <p style={S.foundNote}>{user ? 'You\'re in — start meeting the other guests.' : 'Takes a minute. Free for guests, always.'}</p>

            <button className="tw-nl" onClick={() => { setFoundEvent(null); setEventCode(''); }} style={S.foundBack} data-testid="wrong-wedding-btn">
              Not your wedding? Try another code
            </button>
          </div>
        )}

      </main>

      {/* whisper-quiet footer */}
      <footer style={S.foot} className="tw-chrome2">
        <div style={S.footLinks}>
          {/* navigate() not <a href> — a full reload would replay the whole
              logo animation every time someone comes back from Terms. */}
          <span className="tw-nl-s" onClick={() => navigate('/privacy')} data-testid="footer-privacy">Privacy</span>
          <span className="tw-nl-s" onClick={() => navigate('/terms')} data-testid="footer-terms">Terms</span>
          <span style={S.copyright}>© {new Date().getFullYear()} tweddi</span>
        </div>
      </footer>
    </div>
  );
}

const S = {
  page: { position: 'relative', minHeight: '100dvh', background: INK, color: CREAM, fontFamily: "'Inclusive Sans', system-ui, sans-serif", display: 'flex', flexDirection: 'column', maxWidth: '40rem', margin: '0 auto', overflow: 'hidden' },
  nav: { position: 'relative', zIndex: 5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '24px 28px' },
  brandSmall: { display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' },
  brandSmallWord: { fontSize: 19, fontWeight: 600, letterSpacing: '-0.01em', color: CREAM },
  main: { position: 'relative', zIndex: 4, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '8px 28px 24px', textAlign: 'center' },
  stage: { width: 'min(54vw, 232px)', position: 'relative' },

  sub: { margin: '1.5rem 0 0', fontSize: 'clamp(16px, 2.2vw, 18px)', fontWeight: 600, color: CREAM, letterSpacing: '-0.01em', lineHeight: 1.45 },
  subDim: { display: 'block', marginTop: 6, fontSize: 'clamp(13px, 1.7vw, 14.5px)', fontWeight: 400, color: MUTE, letterSpacing: 0, lineHeight: 1.55, maxWidth: 330 },

  join: { display: 'flex', alignItems: 'center', gap: 8, marginTop: '1.75rem', width: '100%', maxWidth: 360, padding: 7, background: 'rgba(255,253,248,0.06)', border: `1.5px solid ${CREAM}`, borderRadius: 999 },
  input: { flex: 1, minWidth: 0, background: 'transparent', border: 'none', outline: 'none', color: CREAM, fontSize: 16, fontWeight: 600, letterSpacing: '0.2em', padding: '12px 20px', textAlign: 'center', textTransform: 'uppercase', fontFamily: "'Inclusive Sans', sans-serif" },
  go: { width: 48, height: 48, borderRadius: 999, flexShrink: 0, cursor: 'pointer', border: 'none', background: CREAM, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  helper: { margin: '0.85rem 0 0', fontSize: 12.5, fontWeight: 400, color: 'rgba(255,253,248,0.4)', letterSpacing: '0.01em' },

  divider: { display: 'flex', alignItems: 'center', gap: 14, width: '100%', maxWidth: 360, margin: '1.75rem 0 1.5rem' },
  dividerLine: { flex: 1, height: 1, background: 'rgba(255,253,248,0.14)' },
  dividerWord: { fontSize: 10.5, fontWeight: 600, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'rgba(255,253,248,0.35)' },

  hostBtn: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, width: '100%', maxWidth: 360, padding: '13px 8px 13px 18px', background: 'transparent', border: '1.5px solid rgba(255,253,248,0.2)', borderRadius: 999, cursor: 'pointer', fontFamily: "'Inclusive Sans', sans-serif", transition: 'border-color .25s, background .25s' },
  hostLabel: { fontSize: 13.5, fontWeight: 500, color: MUTE, whiteSpace: 'nowrap' },
  hostCta: { display: 'inline-flex', alignItems: 'center', gap: 9, fontSize: 11, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: CREAM, whiteSpace: 'nowrap' },
  hostArrow: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 999, background: CREAM, flexShrink: 0, transition: 'transform .25s cubic-bezier(.16,1,.3,1)' },
  found: { display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: 360, marginTop: '1.75rem' },
  foundEyebrow: { display: 'inline-flex', alignItems: 'center', gap: 7, margin: 0, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(255,253,248,0.5)' },
  foundName: { margin: '0.9rem 0 0', fontSize: 'clamp(1.9rem, 8vw, 2.5rem)', fontWeight: 600, letterSpacing: '-0.03em', lineHeight: 1.08, color: CREAM },
  foundMeta: { display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 8, marginTop: '0.7rem', fontSize: 12.5, fontWeight: 500, color: MUTE },
  metaDot: { width: 3, height: 3, borderRadius: 999, background: 'rgba(255,253,248,0.3)', display: 'inline-block', flexShrink: 0 },
  foundStats: { display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 8, marginTop: '1.1rem', paddingTop: '1.1rem', borderTop: '1px solid rgba(255,253,248,0.1)', width: '100%', fontSize: 12.5, fontWeight: 400, color: MUTE },
  statNum: { color: CREAM, fontWeight: 700 },
  foundCta: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 12, width: '100%', marginTop: '1.75rem', padding: '7px 7px 7px 24px', minHeight: 58, background: CREAM, border: 'none', borderRadius: 999, cursor: 'pointer', fontFamily: "'Inclusive Sans', sans-serif", fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: INK },
  foundArrow: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44, borderRadius: 999, background: 'rgba(20,19,19,0.07)', flexShrink: 0, transition: 'transform .25s cubic-bezier(.16,1,.3,1)' },
  foundNote: { margin: '0.85rem 0 0', fontSize: 12, fontWeight: 400, color: 'rgba(255,253,248,0.4)' },
  foundBack: { marginTop: '1.5rem', fontSize: 12, fontWeight: 500, color: 'rgba(255,253,248,0.4)', textDecoration: 'underline', textUnderlineOffset: 3 },
  priceNote: { display: 'inline-flex', alignItems: 'center', gap: 7, margin: '0.85rem 0 0', fontSize: 12.5, fontWeight: 500, color: 'rgba(255,253,248,0.45)', letterSpacing: '0.01em' },
  priceDot: { width: 3, height: 3, borderRadius: 999, background: APRICOT, display: 'inline-block', flexShrink: 0 },

  proof: { display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: '8px 16px', marginTop: '2rem' },
  proofItem: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 500, letterSpacing: '0.04em', color: 'rgba(255,253,248,0.38)' },
  dot: { width: 3, height: 3, borderRadius: 999, background: APRICOT, display: 'inline-block', flexShrink: 0 },

  foot: { position: 'relative', zIndex: 4, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '22px 28px', borderTop: '1px solid rgba(255,253,248,0.07)', gap: 16, flexWrap: 'wrap' },
  footLinks: { display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' },
  copyright: { fontSize: 11, fontWeight: 500, letterSpacing: '0.06em', color: 'rgba(255,253,248,0.28)', whiteSpace: 'nowrap' },
};

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inclusive+Sans:wght@400;500;600;700&display=swap');
* { box-sizing: border-box; }

:root{
  --bg:${INK}; --mark:${CREAM};
  --t-draw:1.05s; --d-roll:.65s; --t-roll:1.05s; --t-lock:1.70s; --d-word:2.05s;
}

.ring { fill:none; stroke:var(--mark); stroke-width:66; }
.halo { fill:none; stroke:var(--bg);   stroke-width:108; }

/* ACT I — band 1 draws + spins in */
.play #band1 .ring{ stroke-dasharray:100; stroke-dashoffset:100;
  animation:draw var(--t-draw) cubic-bezier(.7,0,.18,1) forwards,
            vanish .01s steps(1) calc(var(--t-draw) + .02s) forwards; }
#band1clean{ opacity:0; }
.play #band1clean{ animation:snap .01s steps(1) var(--t-draw) forwards; }
.play #band1{ transform-box:fill-box; transform-origin:center;
  animation:spinIn var(--t-draw) cubic-bezier(.7,0,.18,1) forwards; }
@keyframes draw{to{stroke-dashoffset:0}}
@keyframes spinIn{from{transform:rotate(-120deg) scale(.86);opacity:0}30%{opacity:1}to{transform:rotate(0) scale(1);opacity:1}}

/* ACT II — band 2 rolls in */
#band2roll{ transform-box:fill-box; transform-origin:center; opacity:0; }
.play #band2roll{ animation:rollIn var(--t-roll) cubic-bezier(.16,.9,.24,1.02) var(--d-roll) forwards; }
@keyframes rollIn{
  0%{transform:translateX(560px) rotate(660deg);opacity:0}
  8%{opacity:1}
  74%{transform:translateX(-14px) rotate(-14deg)}
  86%{transform:translateX(4px) rotate(4deg) scale(1.05,.94)}
  100%{transform:translateX(0) rotate(0) scale(1,1);opacity:1}
}

/* ACT III — the lock */
#lockLayers{ opacity:0; }
.play #lockLayers{ animation:snap .01s steps(1) var(--t-lock) forwards; }
.play #band2roll .ring{ animation:vanish .01s steps(1) calc(var(--t-lock) + .02s) forwards; }
@keyframes snap{to{opacity:1}}
@keyframes vanish{to{opacity:0}}
.play #mark{ transform-box:fill-box; transform-origin:center; animation:lockPulse .5s cubic-bezier(.2,1.6,.35,1) var(--t-lock); }
@keyframes lockPulse{0%{transform:scale(1)}28%{transform:scale(1.055)}62%{transform:scale(.985)}100%{transform:scale(1)}}
.stage.play{ animation:shake .34s linear var(--t-lock); }
@keyframes shake{0%,100%{transform:translate(0,0)}20%{transform:translate(2px,-1.5px)}40%{transform:translate(-2px,1.5px)}60%{transform:translate(1.5px,1px)}80%{transform:translate(-1px,-1px)}}

#ripple,#ripple2{ fill:none; stroke:var(--mark); opacity:0; transform-box:fill-box; transform-origin:center; }
#ripple{ stroke-width:3; } #ripple2{ stroke-width:1.5; }
.play #ripple{ animation:ripple .9s cubic-bezier(.1,.6,.2,1) var(--t-lock) forwards; }
.play #ripple2{ animation:ripple 1.1s cubic-bezier(.1,.6,.2,1) calc(var(--t-lock) + .09s) forwards; }
@keyframes ripple{0%{transform:scale(.2);opacity:.9}100%{transform:scale(1);opacity:0}}

.spark{ stroke:var(--mark); stroke-width:5; stroke-linecap:round; opacity:0; transform-box:fill-box; transform-origin:center; }
.play #sparkA{ animation:sparkA .55s cubic-bezier(.15,.8,.3,1) var(--t-lock) forwards; }
.play #sparkB{ animation:sparkB .55s cubic-bezier(.15,.8,.3,1) calc(var(--t-lock) + .04s) forwards; }
@keyframes sparkA{0%{opacity:1;transform:translate(0,0) scale(.4)}100%{opacity:0;transform:translate(46px,-64px) scale(1)}}
@keyframes sparkB{0%{opacity:1;transform:translate(0,0) scale(.4)}100%{opacity:0;transform:translate(-38px,70px) scale(1)}}

/* ACT IV — wordmark */
.word{ display:flex; justify-content:center; gap:.01em; font-family:'Inclusive Sans',sans-serif; font-weight:600;
  font-size:clamp(34px,10vw,58px); color:var(--mark); margin-top:clamp(18px,4vh,32px); letter-spacing:.02em; }
.word span{ display:inline-block; opacity:0; transform:translateY(.55em) rotate(4deg); }
.play ~ .word span{ animation:rise .6s cubic-bezier(.2,1.4,.3,1) forwards; }
.play ~ .word span:nth-child(1){animation-delay:calc(var(--d-word) + 0s)}
.play ~ .word span:nth-child(2){animation-delay:calc(var(--d-word) + .05s)}
.play ~ .word span:nth-child(3){animation-delay:calc(var(--d-word) + .10s)}
.play ~ .word span:nth-child(4){animation-delay:calc(var(--d-word) + .15s)}
.play ~ .word span:nth-child(5){animation-delay:calc(var(--d-word) + .20s)}
.play ~ .word span:nth-child(6){animation-delay:calc(var(--d-word) + .25s)}
@keyframes rise{to{opacity:1;transform:translateY(0) rotate(0)}}

/* idle breathing */
.breathe #mark{ animation:breathe 6s ease-in-out infinite; transform-box:fill-box; transform-origin:center; }
@keyframes breathe{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}

/* late elements + chrome */
.tw-late, .tw-late2, .tw-late3, .tw-late4 { opacity:0; animation:tw-up .7s cubic-bezier(.16,1,.3,1) forwards; }
.tw-late { animation-delay:2.55s; } .tw-late2 { animation-delay:2.7s; }
.tw-late3 { animation-delay:2.85s; } .tw-late4 { animation-delay:3s; }
@keyframes tw-up{ from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
.tw-chrome{ opacity:0; animation:tw-fade 1s .2s forwards; }
.tw-chrome2{ opacity:0; animation:tw-fade 1.2s 2.9s forwards; }
@keyframes tw-fade{ to{opacity:1} }

.tw-nl{ font-size:13px; font-weight:600; letter-spacing:.05em; text-transform:uppercase; color:${CREAM}; cursor:pointer; transition:opacity .2s; }
.tw-nl:hover{ opacity:.55; }
.tw-nl-s{ font-size:11px; font-weight:500; letter-spacing:.1em; text-transform:uppercase; color:${MUTE}; text-decoration:none; cursor:pointer; transition:color .2s; user-select:none; }
.tw-nl-s:hover{ color:${CREAM}; }
.tw-found{ animation:tw-found-in .55s cubic-bezier(.16,1,.3,1) both; }
@keyframes tw-found-in{ from{opacity:0; transform:translateY(14px) scale(.98)} to{opacity:1; transform:none} }
.tw-go2{ transition:transform .2s; }
.tw-go2:hover{ transform:translateY(-1px); }
.tw-go2:hover span:last-child{ transform:translateX(3px); }
.tw-go2:active{ transform:scale(.99); }
.tw-go2:disabled{ opacity:.55; cursor:default; }

.tw-host:hover{ border-color:rgba(255,253,248,0.5) !important; background:rgba(255,253,248,0.04) !important; }
.tw-host:hover .tw-hostArrow{ transform:translateX(3px); }
.tw-host:active{ transform:scale(.99); }

.tw-go{ transition:transform .2s, box-shadow .3s; box-shadow:0 6px 20px rgba(0,0,0,0.35); }
.tw-go:hover{ transform:scale(1.05); } .tw-go:active{ transform:scale(.97); }
.tw-go:disabled{ opacity:.5; cursor:default; }
.spin{ animation:tw-spin .7s linear infinite; }
@keyframes tw-spin{ to{transform:rotate(360deg)} }
::placeholder{ color:rgba(255,253,248,0.35); }


@media (prefers-reduced-motion: reduce){
  .play *, .play ~ .word span, .tw-late, .tw-late2, .tw-late3, .tw-late4, .tw-chrome, .tw-chrome2{ animation-duration:.01s !important; animation-delay:0s !important; opacity:1 !important; }
  .tw-found{ animation:none; }
  .breathe #mark{ animation:none; }
}
`;
